import { useMemo, useState } from "react";
import { Buildings, MagnifyingGlass, CheckSquare, Square } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

/**
 * Reusable multi-club selector.
 * Renders a searchable checkbox list plus a summary of selected clubs.
 *
 * Props:
 *   clubs: [{id, name}]        — all available clubs
 *   value: [id, id]            — currently selected club ids
 *   onChange: (ids: string[])  — called with new list
 *   testid: string             — optional prefix for data-testid attributes
 *   emptyLabel: string         — label when no clubs selected
 *   maxHeight: string          — tailwind height class for the scroll area
 */
export default function MultiClubSelect({
  clubs = [],
  value = [],
  onChange,
  testid = "multi-club",
  emptyLabel = "Sin clubes seleccionados",
  maxHeight = "max-h-52",
}) {
  const [q, setQ] = useState("");
  const selectedSet = useMemo(() => new Set(value || []), [value]);

  const toggle = (id) => {
    const next = selectedSet.has(id)
      ? (value || []).filter((x) => x !== id)
      : [...(value || []), id];
    onChange?.(next);
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return clubs;
    return clubs.filter((c) => (c.name || "").toLowerCase().includes(needle));
  }, [clubs, q]);

  const selectedClubs = useMemo(
    () => clubs.filter((c) => selectedSet.has(c.id)),
    [clubs, selectedSet]
  );

  return (
    <div className="space-y-2" data-testid={testid}>
      {/* Summary chips */}
      <div className="flex flex-wrap gap-1 min-h-[24px]" data-testid={`${testid}-chips`}>
        {selectedClubs.length === 0 ? (
          <span className="text-[11px] text-gray-500 italic">{emptyLabel}</span>
        ) : (
          selectedClubs.map((c) => (
            <Badge
              key={c.id}
              variant="outline"
              className="bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/30 text-[10px] gap-1"
              data-testid={`${testid}-chip-${c.id}`}
            >
              <Buildings size={10} weight="fill" /> {c.name}
            </Badge>
          ))
        )}
      </div>

      <div className="relative">
        <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar club…"
          className="bg-[#0A0A0A] border-white/20 text-white pl-8 h-9 text-xs focus:border-[#CCFF00]"
          data-testid={`${testid}-search`}
        />
      </div>

      <div
        className={`border border-white/10 bg-black/40 rounded ${maxHeight} overflow-y-auto`}
        data-testid={`${testid}-list`}
      >
        {filtered.length === 0 ? (
          <p className="text-xs text-gray-500 text-center py-4">Sin clubes que coincidan</p>
        ) : (
          filtered.map((c) => {
            const on = selectedSet.has(c.id);
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => toggle(c.id)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs border-b border-white/5 last:border-b-0 transition-colors ${
                  on ? "bg-[#CCFF00]/10 text-[#CCFF00]" : "text-gray-200 hover:bg-white/5"
                }`}
                data-testid={`${testid}-option-${c.id}`}
              >
                {on ? (
                  <CheckSquare size={16} weight="fill" className="text-[#CCFF00] shrink-0" />
                ) : (
                  <Square size={16} className="text-gray-500 shrink-0" />
                )}
                <Buildings size={12} className="text-gray-500 shrink-0" />
                <span className="truncate">{c.name}</span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
